import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import DatePicker from 'react-datepicker';
import { ButtonSwitch } from 'lib/components';
import { combineDateAndTime } from 'lib/utils/datetime';
import { theme } from 'lib/style';
import { IoMdCalendar } from 'react-icons/io';
import { BsClockFill } from 'react-icons/bs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import { Schedule } from 'lib/api/schedule/types';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const CustomTimeWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const InputDateWrapper = styled.div<{ width?: string }>`
  position: relative;
  height: 40px;
  input {
    padding: 6px 8px 8px 40px;
    width: ${({ width }) => width};
    border-radius: 6px;
    border: 1px solid rgba(0, 27, 83, 0.2);
    font-size: 16px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    color: ${theme.palette.gray100};
  }
  .hideCursor {
    caret-color: transparent;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  color: ${theme.palette.gray60};
  top: 8px;
  left: 8px;
  z-index: 1;
`;

const ErrorMessage = styled.div`
  margin-top: 10px;
  color: red;
`;

interface CustomDateTimePickerProps {
  setDate: (date: Date) => void;
  date: Date | null;
  useTimeSelectDropdown?: boolean;
  disabled?: boolean;
  hideDatePicker?: boolean;
  setErrorMessage?: (value: string) => void;
  errorMessage?: string;
  allowCustomTime?: boolean;
  timezone?: string;
  editSchedule?: Schedule | null;
}

const MERIDIEM = {
  AM: 'am',
  PM: 'pm',
};
const meridiemValues = [
  {
    value: MERIDIEM.AM,
    text: 'AM',
  },
  {
    value: MERIDIEM.PM,
    text: 'PM',
  },
];

const isAmPm = (hours?: number) => {
  return hours && hours >= 12 ? MERIDIEM.PM : MERIDIEM.AM;
};

const CustomTimezoneDateTimePicker = ({
  setDate,
  date,
  useTimeSelectDropdown = false,
  disabled = false,
  hideDatePicker = false,
  setErrorMessage,
  errorMessage,
  allowCustomTime = true,
  timezone,
  editSchedule,
}: CustomDateTimePickerProps) => {
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [startTime, setStartTime] = useState<Date>(new Date());
  const [meridiem, setMeridiem] = useState<string>(MERIDIEM.AM);
  const [hasUserModified, setHasUserModified] = useState(false);

  const handleDateTimeChange = (
    startDate: Date,
    startTime: Date,
    meridiem: string
  ) => {
    const combinedDateTime = new Date(startDate);
    combinedDateTime.setHours(
      meridiem === MERIDIEM.PM
        ? startTime.getHours() + 12
        : startTime.getHours(),
      startTime.getMinutes()
    );
    const current = new Date();
    if (
      startDate.toDateString() === current.toDateString() &&
      combinedDateTime < current
    ) {
      if (setErrorMessage) {
        setErrorMessage(
          'Invalid selection. Please choose a time that is in the future.'
        );
      }
      return;
    }

    setDate(combineDateAndTime(startDate, startTime, meridiem));
  };

  const parseDate = (date: Date) => {
    return new Date(Date.parse(date as unknown as string));
  };

  useEffect(() => {
    if (date) {
      setMeridiem(isAmPm(parseDate(date).getHours()));
      setStartDate(parseDate(date));
      setStartTime(parseDate(date));
    }
  }, [date]);

  useEffect(() => {
    if (!timezone || !startDate) return;

    let newTime: Date;
    let newMeridiem: string;

    if (editSchedule?.deliveryTime && !hasUserModified) {
      setHasUserModified(true);
      const [dateStr, timeStr, meridiemStr] =
        editSchedule.deliveryTime.split(';');

      // Extract hour and minute manually from timeStr
      const [hourStr, minuteStr] = timeStr.split(':');
      let hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);

      if (meridiemStr.toUpperCase() === 'PM' && hour < 12) {
        hour += 12;
      } else if (meridiemStr.toUpperCase() === 'AM' && hour === 12) {
        hour = 0;
      }

      // Create Date object at 8:00 AM in selected timezone, not affected by local conversion
      const baseDate = dayjs.tz(dateStr, 'YYYY-MM-DD', timezone);
      const zonedDate = baseDate.hour(hour).minute(minute).second(0);

      // Convert to JS Date *as-is* for DatePicker (shows time in that zone)
      const preservedDate = new Date(
        zonedDate.year(),
        zonedDate.month(),
        zonedDate.date(),
        zonedDate.hour(),
        zonedDate.minute(),
        0
      );

      newTime = preservedDate;
      newMeridiem = meridiemStr.toLowerCase();
      setStartDate(preservedDate);
    } else {
      const minTime = getMinTime();
      newTime = minTime;
      newMeridiem = isAmPm(minTime.getHours());
    }

    setMeridiem(newMeridiem);
    setStartTime(newTime);
    handleDateTimeChange(startDate, newTime, newMeridiem);
  }, [timezone]);

  const handleDateChange = (
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    if (!event) return;

    const input = event.target as HTMLInputElement;
    const inputValue = input.value || startDate.toString();
    if (setErrorMessage) {
      setErrorMessage(
        isNaN(Date.parse(inputValue)) ? 'Please enter a valid date/time' : ''
      );
    }
  };

  const handleTimeChange = (
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    if (!event) return;

    const input = event.target as HTMLInputElement;
    const inputValue = input.value;
    if (setErrorMessage) {
      setErrorMessage(
        !/^(0?[1-9]|1[0-2]):[0-5][0-9]$/.test(inputValue)
          ? 'Please enter a valid date/time'
          : ''
      );
    }
  };

  const getMinTime = () => {
    const currentTime = dayjs().tz(timezone);
    const selectedDate = dayjs(startDate).tz(timezone, true);
    // Only calculate nearest interval if selected date is today in the selected timezone
    if (
      currentTime.format('YYYY-MM-DD') === selectedDate.format('YYYY-MM-DD')
    ) {
      const nextIntervalMinutes = Math.ceil(currentTime.minute() / 15) * 15;
      const nearestNextInterval = currentTime
        .startOf('hour')
        .add(nextIntervalMinutes, 'minutes');

      // Convert dayjs to JS Date in local time (for react-datepicker)
      return new Date(
        nearestNextInterval.year(),
        nearestNextInterval.month(),
        nearestNextInterval.date(),
        nearestNextInterval.hour(),
        nearestNextInterval.minute(),
        0
      );
    }

    // For future dates, min time is midnight in the selected timezone
    const midnight = selectedDate.startOf('day');
    return new Date(
      midnight.year(),
      midnight.month(),
      midnight.date(),
      0,
      0,
      0
    );
  };

  const getMaxTime = () => {
    return new Date(startDate.setHours(23, 45)); // 11:45 PM for future dates
  };

  const canSelectAM = () => {
    const amTime = new Date(startTime);
    amTime.setHours(amTime.getHours() % 12); // Adjust to AM hour
    return amTime.getTime() >= getMinTime().getTime();
  };

  const canSelectPM = () => {
    const pmTime = new Date(startTime);
    pmTime.setHours((pmTime.getHours() % 12) + 12); // Adjust to PM hour
    return pmTime.getTime() >= getMinTime().getTime();
  };

  const canSelectAMPM = () => {
    return canSelectAM() && canSelectPM();
  };

  return (
    <>
      <CustomTimeWrapper>
        {!hideDatePicker && (
          <InputDateWrapper width='97px'>
            <IconWrapper>
              <IoMdCalendar size={18} />
            </IconWrapper>

            <DatePicker
              onChange={(date: Date | null) => {
                if (!date) {
                  return;
                }
                handleDateTimeChange(date, startTime, meridiem);
              }}
              onChangeRaw={handleDateChange}
              selected={startDate}
              minDate={new Date()}
              disabled={disabled}
            />
          </InputDateWrapper>
        )}
        <InputDateWrapper width='40px'>
          <IconWrapper>
            <BsClockFill size={22} />
          </IconWrapper>
          <DatePicker
            selected={startTime}
            onChange={(time: Date | null) => {
              if (!time) {
                return;
              }
              handleDateTimeChange(startDate, time, meridiem);
            }}
            onChangeRaw={handleTimeChange}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeFormat='h:mm'
            timeCaption='Time'
            dateFormat='h:mm'
            minTime={getMinTime()}
            maxTime={getMaxTime()}
            open={useTimeSelectDropdown ? undefined : false}
            disabled={disabled}
            {...(!allowCustomTime && { onKeyDown: e => e.preventDefault() })}
            {...(!allowCustomTime && { className: 'hideCursor' })}
          />
        </InputDateWrapper>
        <InputDateWrapper width='23px'>
          <ButtonSwitch
            key={meridiem}
            defaultValue={meridiem}
            values={meridiemValues}
            onChange={newValue => {
              setMeridiem(newValue);
              handleDateTimeChange(startDate, startTime, newValue);
            }}
            disabled={disabled || !canSelectAMPM()}
          />
        </InputDateWrapper>
      </CustomTimeWrapper>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </>
  );
};

export default CustomTimezoneDateTimePicker;
